import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Typography,
  Pagination,
  IconButton,
  Tooltip,
  alpha,
} from "@mui/material";
import dayjs from "dayjs";
import axios from "axios";
import { WEBSERVICE } from "../../../CoreDataService";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

const PortfolioHistory = () => {
  const { btncolor } = useSelector((state) => state);
  const [portfolioAssets, setPortfolioAssets] = useState([]);
  const [recurringPeriods, setRecurringPeriods] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    getPortfolioAssets();
    getRecurringPeriods();
    fetchPortfolioHistory();
  }, [pageNo]);

  const fetchPortfolioHistory = async () => {
    const payload = {
      userUuid: localStorage.getItem("uuid"),
      pageNo,
      pageSize,
      fromDate: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "1970-01-01",
      toDate: toDate
        ? dayjs(toDate).format("YYYY-MM-DD")
        : dayjs(new Date()).format("YYYY-MM-DD"),
      invCurrencyId: selectedAsset || 0,
      recurringPeriodId: selectedPeriod || 0,
    };

    try {
      const response = await axios.post(
        WEBSERVICE +
          `/crypto/banking/portfolio/searchPortfolioInvestmentHistories`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.error.error_data === 0) {
        setHistoryRows(response.data.portfolioInvestmentHistories || []);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (err) {
      console.error("Error fetching history", err);
    }
  };

  const getPortfolioAssets = async () => {
    try {
      const response = await axios.post(
        WEBSERVICE + `/crypto/banking/portfolio/getPortfolioInvestmentAssets`,
        { userUuid: localStorage.getItem("uuid") },
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.error.error_data === 0) {
        setPortfolioAssets(
          response.data.portfolioAssets.filter((a) => a.isBaseCurrency === 0)
        );
      }
    } catch (err) {
      console.error("Error fetching assets", err);
    }
  };

  const getRecurringPeriods = async () => {
    try {
      const response = await axios.get(
        WEBSERVICE +
          `/crypto/banking/portfolio/getPortfolioRecurringPeriods?uuid=${localStorage.getItem(
            "uuid"
          )}`,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.error.error_data === 0) {
        setRecurringPeriods(response.data.portfolioRecurringPeriods);
      }
    } catch (err) {
      console.error("Error fetching periods", err);
    }
  };

  const handleReset = () => {
    setSelectedAsset("");
    setSelectedPeriod("");
    setFromDate(null);
    setToDate(null);
    setPageNo(1);
    fetchPortfolioHistory();
  };

  const tableColumns = [
    "Auto-Invest Date",
    "Plan Name",
    "Portfolio Asset",
    "Recurring Period",
    "Invested per Period",
    "Trading Fee",
    "Units Purchased",
    "Price",
    "Wallet",
    "Status",
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={pageWrapper}>

        {/* ── Hero Banner ── */}
        <Box
          sx={{
            ...heroBanner,
            background: `linear-gradient(135deg, ${alpha(
              btncolor || "#1976d2",
              0.95
            )} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
          }}
        >
          <Box sx={heroContent}>
            <Box sx={heroIconBadge}>
              <HistoryOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
            </Box>
            <Box>
              <Typography sx={heroTitle}>Portfolio History</Typography>
              <Typography sx={heroSubtitle}>
                Track your auto-invest transactions, units purchased and plan performance over time
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── Main Card ── */}
        <Box sx={mainCard}>

          {/* ── Filter Section ── */}
          <Box sx={filterCard}>
            <Box sx={filterHeader}>
              <FilterAltOutlinedIcon sx={{ ...filterHeaderIcon, color: btncolor }} />
              <Typography sx={filterHeaderText}>Filter History</Typography>
            </Box>

            <Box sx={filterGrid}>
              {/* Portfolio Asset */}
              <Box sx={fieldWrap}>
                <Typography sx={fieldLabel}>
                  <DescriptionOutlinedIcon sx={fieldLabelIcon} /> Portfolio Asset
                </Typography>
                <TextField
                  select
                  value={selectedAsset}
                  onChange={(e) => setSelectedAsset(e.target.value)}
                  sx={modernFieldStyle}
                  fullWidth
                >
                  <MenuItem value="">All</MenuItem>
                  {portfolioAssets.map((a) => (
                    <MenuItem key={a.currencyId} value={a.currencyId}>
                      {a.currencyCode}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Recurring Period */}
              <Box sx={fieldWrap}>
                <Typography sx={fieldLabel}>
                  <DescriptionOutlinedIcon sx={fieldLabelIcon} /> Recurring Period
                </Typography>
                <TextField
                  select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  sx={modernFieldStyle}
                  fullWidth
                >
                  <MenuItem value="">All</MenuItem>
                  {recurringPeriods.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* From Date */}
              <Box sx={fieldWrap}>
                <Typography sx={fieldLabel}>
                  <EventOutlinedIcon sx={fieldLabelIcon} /> From Date
                </Typography>
                <DatePicker
                  value={fromDate}
                  onChange={setFromDate}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      sx: modernFieldStyle,
                      fullWidth: true,
                    },
                  }}
                />
              </Box>

              {/* To Date */}
              <Box sx={fieldWrap}>
                <Typography sx={fieldLabel}>
                  <EventOutlinedIcon sx={fieldLabelIcon} /> To Date
                </Typography>
                <DatePicker
                  value={toDate}
                  onChange={setToDate}
                  minDate={fromDate}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      sx: modernFieldStyle,
                      fullWidth: true,
                    },
                  }}
                />
              </Box>

              {/* Action Buttons */}
              <Box sx={actionBtnWrap}>
                <Tooltip title="Search" arrow>
                  <IconButton
                    onClick={fetchPortfolioHistory}
                    sx={{
                      ...actionIconBtn,
                      backgroundColor: btncolor,
                      "&:hover": { backgroundColor: btncolor, opacity: 0.88 },
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset Filters" arrow>
                  <IconButton
                    onClick={handleReset}
                    sx={{
                      ...actionIconBtn,
                      backgroundColor: "rgba(125,125,125,0.15)",
                      "&:hover": { backgroundColor: "rgba(125,125,125,0.25)" },
                    }}
                  >
                    <RestartAltIcon sx={{ fontSize: 20, color: "var(--text-color)" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* ── Results Header ── */}
          <Box sx={resultsHeader}>
            <Box>
              <Typography sx={resultsTitle}>Investment History</Typography>
              <Typography sx={resultsCaption}>
                Showing {historyRows.length} of {totalCount} records
              </Typography>
            </Box>
          </Box>

          {/* ── Table ── */}
          <TableContainer component={Paper} sx={modernTableContainer}>
            <Table sx={tableStyle}>
              <TableHead>
                <TableRow sx={modernTableHeadRow}>
                  {tableColumns.map((col, idx) => (
                    <TableCell key={idx} sx={modernTableHeadCell}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRows.length > 0 ? (
                  historyRows.map((row, idx) => (
                    <TableRow key={idx} sx={modernTableBodyRow}>
                      <TableCell sx={modernTableBodyCell}>{row.autoInvestDate}</TableCell>
                      <TableCell sx={modernTableBodyCell}>{row.planName}</TableCell>
                      <TableCell sx={modernTableBodyCell}>{row.assetCode}</TableCell>
                      <TableCell sx={modernTableBodyCell}>{row.recurringPeriod}</TableCell>
                      <TableCell sx={modernTableBodyCell}>{`${row.investedPerPeriod} ${row.invAsset}`}</TableCell>
                      <TableCell sx={modernTableBodyCell}>{`${row.tradingFee} ${row.invAsset}`}</TableCell>
                      <TableCell sx={modernTableBodyCell}>{`${row.unitPurchased} ${row.assetCode}`}</TableCell>
                      <TableCell sx={modernTableBodyCell}>{`${row.pricePerUnit} ${row.invAsset}`}</TableCell>
                      <TableCell sx={modernTableBodyCell}>{row.walletDesc}</TableCell>
                      <TableCell sx={modernTableBodyCell}>{row.status}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length} sx={emptyCell}>
                      <InboxOutlinedIcon sx={emptyIcon} />
                      <Typography sx={emptyText}>No history records found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Pagination ── */}
          {totalCount > pageSize && (
            <Box sx={paginationWrap}>
              <Typography sx={paginationCaption}>
                Page {pageNo} of {Math.ceil(totalCount / pageSize)}
              </Typography>
              <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={pageNo}
                onChange={(e, value) => setPageNo(value)}
                sx={paginationStyle}
              />
            </Box>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

// ── Styles (mirrors Report.js / RecurringPayment.js conventions) ──────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 640,
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
  p: { xs: 2, md: 3 },
};

const filterCard = {
  borderRadius: "12px",
  mb: 3,
};

const filterHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 2,
};

const filterHeaderIcon = {
  fontSize: 18,
};

const filterHeaderText = {
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "var(--text-color)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const filterGrid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr auto" },
  gap: 2,
  alignItems: "end",
};

const fieldWrap = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  minWidth: 0,
};

const fieldLabel = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "var(--text-color)",
  opacity: 0.7,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  display: "flex",
  alignItems: "center",
  gap: 0.5,
};

const fieldLabelIcon = {
  fontSize: 14,
  color: "var(--text-color)",
  opacity: 0.7,
};

const modernFieldStyle = {
  width: "100%",
  "& .MuiInputBase-root": {
    height: "48px",
    borderRadius: "10px",
    backgroundColor: "var(--body-color)",
    color: "var(--text-color)",
    fontSize: "0.9rem",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiSelect-select": { color: "var(--text-color)" },
  "& .MuiSvgIcon-root": { color: "var(--text-color)", opacity: 0.7 },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.45)",
  },
  "& .MuiInputLabel-root": { display: "none" },
  "& legend": { display: "none" },
  "& fieldset": { top: 0 },
};

const actionBtnWrap = {
  display: "flex",
  alignItems: "flex-end",
  gap: 1,
  pb: "0px",
};

const actionIconBtn = {
  width: 48,
  height: 48,
  borderRadius: "10px",
  color: "#fff",
  flexShrink: 0,
};

const resultsHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 1.5,
  mb: 2,
  pb: 2,
  borderBottom: "1px solid rgba(125,125,125,0.13)",
};

const resultsTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const resultsCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.65,
  mt: 0.25,
};

const modernTableContainer = {
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "none",
  overflow: "hidden",
  backgroundColor: "var(--body-color-less)",
};

const tableStyle = {
  minWidth: 900,
};

const modernTableHeadRow = {
  backgroundColor: "var(--body-color-less2)",
};

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
  whiteSpace: "nowrap",
};

const emptyCell = {
  textAlign: "center",
  py: 7,
  borderBottom: 0,
};

const emptyIcon = {
  fontSize: 48,
  color: "var(--text-color)",
  opacity: 0.35,
  mb: 1,
};

const emptyText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.65,
};

const paginationWrap = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 1,
  mt: 2.5,
  pt: 2,
  borderTop: "1px solid rgba(125,125,125,0.13)",
};

const paginationCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.55,
};

const paginationStyle = {
  "& .MuiPaginationItem-root": {
    color: "var(--text-color)",
    borderColor: "rgba(125,125,125,0.25)",
    fontSize: "0.82rem",
    borderRadius: "8px",
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    backgroundColor: "var(--btn-color, #1976d2)",
    color: "#fff",
    borderColor: "transparent",
  },
  "& .MuiPaginationItem-root:hover": {
    backgroundColor: "rgba(125,125,125,0.12)",
  },
};

export default PortfolioHistory;
